/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
/* eslint-disable @typescript-eslint/no-explicit-any */

import {
  CreateSavedSearchApiResponse,
  ReadSavedSearchApiResponse,
  ReadSavedSearchesApiResponse,
  ReadSearchApiResponse,
  SavedSearch,
  SearchScope,
  UpdateSavedSearchApiResponse,
} from 'lib-search-types';
import { convertChannelCasing } from 'lib-shared-api-utils';

import { emptyFilters } from 'app/views/layout/views/search/components/filters/utils/definitions';
import { enrichStaticFilters } from 'app/views/layout/views/search/utils/filters/enrichStaticFilters';

/** Transforms the raw search response, applying scope-specific transformations. */
export function transformReadSearchResponse<
  TScope extends SearchScope,
  TEnriched extends boolean,
>(response: any, scope: TScope): ReadSearchApiResponse<TScope, TEnriched> {
  if (scope === 'conversations') {
    response.data.results.forEach(({ result }) => {
      result.initial_channel = convertChannelCasing(
        result.initial_channel as string,
        'snake',
      );
    });
  }

  if (scope === 'channels') {
    response.data.results = response.data.results.flatMap(
      ({ result, ...rest }) =>
        result.channels.map((channel: string) => ({
          ...rest,
          result: convertChannelCasing(channel, 'snake'),
        })),
    );
  }

  return response;
}

/** Transforms the read saved search list endpoint response. */
export function transformReadSavedSearchesResponse(
  response: any,
): ReadSavedSearchesApiResponse {
  // Exclude saved searches that use legacy contact filters (no longer supported).
  response.data.savedSearches = response.data.savedSearches.filter(
    (savedSearch: SavedSearch) =>
      !savedSearch.searchRequest?.filters?.filters?.some((condition: any) =>
        ['user_type', 'company_id', 'contact_custom_attribute'].includes(
          condition.field,
        ),
      ),
  );

  response.data.savedSearches.forEach(transformSavedSearch);
  return response;
}

/** Transforms the read saved search endpoint response. */
export function transformReadSavedSearchResponse(
  response: any,
): ReadSavedSearchApiResponse {
  transformSavedSearch(response.data.savedSearch);
  return response;
}

/** Transforms the create saved search endpoint response. */
export function transformCreateSavedSearchResponse(
  response: any,
): CreateSavedSearchApiResponse {
  transformSavedSearch(response.data.savedSearch);
  return response;
}

/** Transforms the update saved search endpoint response. */
export function transformUpdateSavedSearchResponse(
  response: any,
): UpdateSavedSearchApiResponse {
  transformSavedSearch(response.data.savedSearch);
  return response;
}

/** Transforms a saved search object. */
function transformSavedSearch(savedSearch: any): SavedSearch {
  savedSearch.searchRequest.query = savedSearch.searchRequest.query ?? '';

  savedSearch.searchRequest.filters = savedSearch.searchRequest.filters
    ? enrichStaticFilters(savedSearch.searchRequest.filters)
    : emptyFilters;

  return savedSearch;
}
